import type { ConnectionNode } from "@hub/shared";

/**
 * The connections map's layout: the person in the middle, the strongest ten on an inner ring, the rest on an outer
 * one, each node sized by its weight. Pure, so the phone's layout can follow the same rules.
 */
export interface RadialPlacement {
  id: string;
  x: number;
  y: number;
  r: number;
  ring: 1 | 2;
}

export interface RadialLayout {
  center: { x: number; y: number; r: number };
  nodes: RadialPlacement[];
}

export const INNER_RING = 10;

export function radialLayout(nodes: { id: string; weight: number }[], width: number, height: number): RadialLayout {
  const cx = width / 2;
  const cy = height / 2;
  const centerR = Math.max(18, Math.min(width, height) * 0.055);
  const maxW = Math.max(1, ...nodes.map((n) => n.weight));
  const minR = Math.max(10, Math.min(width, height) * 0.028);
  const maxR = Math.max(minR + 2, Math.min(width, height) * 0.045);
  // One ring while it fits; past that the strongest ten move inside, so a lone pair never sits on an outer ring.
  const split = nodes.length > INNER_RING + 2;
  const inner = split ? nodes.slice(0, INNER_RING) : nodes;
  const outer = split ? nodes.slice(INNER_RING) : [];
  const outerRadius = Math.min(width, height) / 2 - maxR - 18;
  const innerRadius = outer.length ? outerRadius * 0.55 : outerRadius;
  const place = (list: { id: string; weight: number }[], radius: number, ring: 1 | 2, offset: number): RadialPlacement[] =>
    list.map((n, i) => {
      const a = -Math.PI / 2 + offset + (i / Math.max(1, list.length)) * Math.PI * 2;
      return { id: n.id, x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius, r: minR + (maxR - minR) * Math.sqrt(n.weight / maxW), ring };
    });
  return {
    center: { x: cx, y: cy, r: centerR },
    nodes: [...place(inner, innerRadius, 1, 0), ...place(outer, outerRadius, 2, outer.length ? Math.PI / Math.max(1, outer.length) : 0)],
  };
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function lastIn(iso: string | null, now: Date): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const month = MONTHS[d.getMonth()];
  return d.getFullYear() === now.getFullYear() ? `, last in ${month}` : `, last in ${month} ${d.getFullYear()}`;
}

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/** Why someone is on the map, in one line: "12 meetings and 3 emails together, last in October; colleague; both in Investors; on Hub". */
export function whyText(node: Pick<ConnectionNode, "events" | "emails" | "lastDate" | "labels" | "groups" | "onHub">, isSelf: boolean, now = new Date()): string {
  const parts: string[] = [];
  const shared: string[] = [];
  if (node.events > 0) shared.push(count(node.events, "meeting", "meetings"));
  if (node.emails > 0) shared.push(count(node.emails, "email", "emails"));
  if (shared.length) parts.push(`${shared.join(" and ")} ${isSelf ? "with you" : "together"}${lastIn(node.lastDate, now)}`);
  if (node.labels.length) parts.push(node.labels.join(", "));
  if (node.groups.length) parts.push(`both in ${node.groups.join(" and ")}`);
  if (node.onHub) parts.push("on Hub");
  return parts.join("; ") || "No shared history yet";
}
